// MFA step of the login flow, extracted from Adminlogin so the two MFA states
// — first-time SETUP (QR + instructions, gated by `qrCodeImgUrl`) and plain
// code ENTRY — live in one place and can be styled independently of the login
// form. Behaviour preserved from the original inline implementation; the OTP
// field is now mui-one-time-password-input (numeric keypad + OS one-time-code
// autofill + box-level error state), and VERIFY has an in-flight guard.
import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { MuiOtpInput } from 'mui-one-time-password-input';
import MFASteps from './MFASteps';

const CODE_LENGTH = 6;

const AuthenticatorInfo = () => (
  <Grid size={12} sx={{ textAlign: 'center' }}>
    <Typography variant="body1" color="textSecondary" gutterBottom>
      Open your authenticator app to get the code. If you don’t have one,
      download:
    </Typography>
    <Typography variant="body1">
      Microsoft Authenticator:{' '}
      <a
        href="https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en_IN&gl=US"
        target="_blank"
        rel="noopener noreferrer"
      >
        Android
      </a>{' '}
      |{' '}
      <a
        href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458"
        target="_blank"
        rel="noopener noreferrer"
      >
        iOS
      </a>
    </Typography>
    <Typography variant="body1">
      Google Authenticator:{' '}
      <a
        href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en"
        target="_blank"
        rel="noopener noreferrer"
      >
        Android
      </a>{' '}
      |{' '}
      <a
        href="https://apps.apple.com/us/app/google-authenticator/id388497605"
        target="_blank"
        rel="noopener noreferrer"
      >
        iOS
      </a>
    </Typography>
  </Grid>
);

export default function MfaVerify({
  code,
  setCode,
  onVerify,
  verifying,
  onCancel,
  codeError,
  qrCodeImgUrl,
  showBackup,
  onBackupCode,
  backupLoading,
  backupDisabled
}) {
  const isSetup = !!qrCodeImgUrl;

  return (
    <Box
      component="form"
      onSubmit={onVerify}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mt: 2
      }}
    >
      <Grid
        container
        spacing={3}
        sx={{ justifyContent: 'center', alignItems: 'center' }}
      >
        {isSetup && (
          <>
            <MFASteps />
            <Grid sx={{ textAlign: 'center' }} size={12}>
              <Box
                component="img"
                src={qrCodeImgUrl}
                alt="Authenticator setup QR code"
                sx={{ maxWidth: '100%', height: 'auto' }}
              />
            </Grid>
          </>
        )}

        {codeError && (
          <Grid sx={{ textAlign: 'center' }} size={12}>
            <Alert severity={codeError?.type ?? 'error'}>
              {codeError?.msg}
            </Alert>
          </Grid>
        )}

        <Grid sx={{ textAlign: 'center' }} size={12}>
          <Typography
            id="mfa-code-label"
            component="p"
            sx={{ fontSize: '1.25rem' }}
          >
            Enter your 6 digit Authentication Code
          </Typography>
        </Grid>

        <Grid sx={{ textAlign: 'center' }} size={12}>
          <MuiOtpInput
            length={CODE_LENGTH}
            value={code}
            onChange={setCode}
            autoFocus
            validateChar={(char) => /^[0-9]$/.test(char)}
            TextFieldsProps={{
              'aria-labelledby': 'mfa-code-label',
              error: !!codeError,
              slotProps: {
                htmlInput: {
                  inputMode: 'numeric',
                  autoComplete: 'one-time-code',
                  'aria-label': 'One-time code digit',
                  // Entered digits one step larger than the default input
                  // font (1rem) and bold, for readability.
                  style: { fontSize: '1.25rem', fontWeight: 700 }
                }
              }
            }}
            sx={{ gap: 1, justifyContent: 'center' }}
          />
        </Grid>

        <AuthenticatorInfo />

        <Grid sx={{ textAlign: 'center' }} size={12}>
          <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
            <Grid>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                aria-label="Verify"
                disabled={code?.length !== CODE_LENGTH || verifying}
              >
                {verifying ? <CircularProgress size={24} /> : 'Verify'}
              </Button>
            </Grid>
            <Grid>
              <Button variant="outlined" color="secondary" onClick={onCancel}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {showBackup && (
          <Grid sx={{ textAlign: 'center' }} size={12}>
            <Button
              type="button"
              variant="text"
              onClick={onBackupCode}
              disabled={backupDisabled}
              sx={{ px: 2.5, py: 1.25, width: 'auto', boxShadow: 'none' }}
            >
              {backupLoading ? (
                <CircularProgress size={24} />
              ) : (
                'Lost your device? Request one time passcode.'
              )}
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
